import {
  AlignContent,
  AlignItems,
  AlignSelf,
  BorderStyle,
  BoxSizing,
  Display,
  FlexDirection,
  FlexWrap,
  FontStyle,
  FontWeight,
  JustifyContent,
  Overflow,
  OverflowWrap,
  Position,
  TextAlign,
  TextDecorationLine,
  TextDecorationStyle,
  TextOverflow,
  Visibility,
  Whitespace,
  WordBreak
} from './types'

export interface Accessible {
  accessibilityId?: string
}

export type StyleOrNot = Style | undefined | null | boolean
export type Styled = {style?: StyleOrNot}

export interface Props<S = StyleOrNot> extends Accessible {
  /**
   * It looks like we can't use "ref" property to pass "ref" down:
   *
   *   Warning: Unknown: `ref` is not a prop. Trying to access it will result in `undefined` being returned.
   *   If you need to access the same value within the child component, you should pass it as a different prop.
   *   (https://fb.me/react-special-props)
   *
   * So "refProxy" is used instead.  To set "ref" on a primitive View, use "refProxy".
   */
  style?: S
  refProxy?: React.Ref<any>
  key?: React.Key
  children?: React.ReactNode
  tooltip?: string
  tooltipDelay?: number
  className?: string

  onMouseOver?: (evt: React.MouseEvent) => void
  onMouseLeave?: (evt: React.MouseEvent) => void
  onMouseDown?: (evt: React.MouseEvent) => void
  onMouseUp?: (evt: React.MouseEvent) => void
  onMouseMove?: (evt: React.MouseEvent) => void
  onFocus?: (evt: React.FocusEvent) => void
  onBlur?: (evt: React.FocusEvent) => void
  onKeyDown?: (evt: React.KeyboardEvent) => void
  tabIndex?: number
}

export interface Style {
  alignContent?: AlignContent
  alignItems?: AlignItems
  alignSelf?: AlignSelf
  backgroundColor?: string
  borderBottomColor?: string
  borderBottomLeftRadius?: number | string
  borderBottomRightRadius?: number | string
  borderBottomWidth?: number | string
  borderColor?: string
  borderLeftColor?: string
  borderLeftWidth?: number | string
  borderRadius?: number | string
  borderRightColor?: string
  borderRightWidth?: number | string
  borderStyle?: BorderStyle
  borderTopColor?: string
  borderTopLeftRadius?: number | string
  borderTopRightRadius?: number | string
  borderTopWidth?: number | string
  borderWidth?: number | string
  bottom?: number | string
  flexBasis?: number | string
  flexDirection?: FlexDirection
  flexGrow?: number
  flexShrink?: number
  flexWrap?: FlexWrap
  height?: number | string
  justifyContent?: JustifyContent
  left?: number | string
  margin?: number | string
  marginBottom?: number | string
  marginHorizontal?: number | string
  marginLeft?: number | string
  marginRight?: number | string
  marginTop?: number | string
  marginVertical?: number | string
  maxHeight?: number | string
  maxWidth?: number | string
  minHeight?: number | string
  minWidth?: number | string
  opacity?: number
  padding?: number | string
  paddingBottom?: number | string
  paddingHorizontal?: number | string
  paddingLeft?: number | string
  paddingRight?: number | string
  paddingTop?: number | string
  paddingVertical?: number | string
  position?: Position
  right?: number | string
  top?: number | string
  visibility?: Visibility
  width?: number | string
  zIndex?: number

  boxShadow?: string
  boxSizing?: BoxSizing
  cursor?: string
  display?: Display
  flex?: number | string
  textOverflow?: TextOverflow
  overflow?: Overflow
  overflowX?: Overflow
  overflowY?: Overflow
  transform?: string
  whiteSpace?: Whitespace
  outline?: string

  // Text styles
  color?: string
  fontFamily?: string
  fontSize?: number | string
  fontStyle?: FontStyle
  fontWeight?: FontWeight
  letterSpacing?: number
  lineHeight?: number | string
  textAlign?: TextAlign
  textDecorationLine?: TextDecorationLine
  textDecorationColor?: string
  textDecorationStyle?: TextDecorationStyle
  textShadowColor?: string
  textShadowOffset?: {width: number; height: number}
  textShadowRadius?: number
  overflowWrap?: OverflowWrap
  wordBreak?: WordBreak
}

export type Styles<S = Style> = S | boolean | undefined | null
